import React, { useEffect, useMemo, useState } from 'react';
import { Table, Container, Pagination, Spinner, Alert, Button } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';
import InlineNotice from '../components/InlineNotice';
import { useInlineNotice } from '../contexts/InlineNoticeContext';
import usePermissions from '../utils/usePermissions';
import ConfirmDialog from '../components/ConfirmDialog';
import UserModal from '../components/UserModal';
import '../styles/CustomerList.css';

const UserListPage = () => {
  const { can } = usePermissions('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { showInlineNotice } = useInlineNotice();
  const permissions = useMemo(() => ({
    canCreate: can('create'),
    canEdit: can('edit'),
    canDeactivate: can('deactivate')
  }), [can]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    column: 'name',
    direction: 'asc'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });

  // Delete confirmation states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/users?page=${page}&limit=${pagination.limit}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setUsers(response.data.users);
      setPagination(response.data.pagination);
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      const errorMessage = err.response?.data?.error || err.message;
      setError(`Failed to load users: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if user is admin
    fetchUsers();
  }, []);

  const handlePageChange = (page) => {
    fetchUsers(page);
  };

  const handleReload = async () => {
    setSelectedRows([]);
    await fetchUsers(pagination.page);
  };

  const handleCheckboxChange = (userId) => {
    setSelectedRows(prevSelected => {
      if (prevSelected.includes(userId)) {
        return prevSelected.filter(id => id !== userId);
      } else {
        return [...prevSelected, userId];
      }
    });
  };

  const handleSort = (column) => {
    setSortConfig(prevConfig => ({
      column,
      direction: prevConfig.column === column && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Delete user
  const handleDeleteUser = () => {
    if (selectedRows.length === 1 && permissions.canDeactivate) {
      const user = users.find(u => u._id === selectedRows[0]);
      setUserToDelete(user);
      setShowDeleteConfirm(true);
    }
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/users/${userToDelete._id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      const name = userToDelete?.name || 'User';
      showInlineNotice(`User ${name} deleted successfully.`, { scope: 'users' });
      setSelectedRows([]);
      // Refresh the user list
      fetchUsers(pagination.page);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.error || 'Failed to delete user');
      showInlineNotice('Failed to delete user.', {
        scope: 'users',
        type: 'error'
      });
      setShowDeleteConfirm(false);
    }
  };

  // View selected user
  const handleViewUser = () => {
    if (selectedRows.length === 1) {
      const user = users.find(u => u._id === selectedRows[0]);
      setSelectedUser(user);
      setShowUserModal(true);
    }
  };

  // Add or update user
  const handleSaveUser = async (formData) => {
    try {
      const token = localStorage.getItem('token');
      if (selectedUser && !permissions.canEdit) {
        setError('You do not have permission to edit users.');
        return;
      }

      if (!selectedUser && !permissions.canCreate) {
        setError('You do not have permission to add users.');
        return;
      }
      const url = selectedUser 
        ? `${API_BASE_URL}/users/${selectedUser._id}`
        : `${API_BASE_URL}/users`;
      
      const method = selectedUser ? 'put' : 'post';
      
      await axios({
        method,
        url,
        data: formData,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const name = selectedUser?.name || formData.name || 'User';
      showInlineNotice(`User ${name} ${selectedUser ? 'updated' : 'created'} successfully.`, { scope: 'users' });
      // Refresh the user list
      fetchUsers(pagination.page);
      setShowUserModal(false);
      setSelectedUser(null);
      setError(null);
    } catch (err) {
      console.error('Error saving user:', err);
      showInlineNotice('Failed to save user.', {
        scope: 'users',
        type: 'error'
      });
      throw err; // Re-throw for modal to handle
    }
  };

  const handleOpenUserModal = (user = null) => {
    if (user && !permissions.canEdit) return;
    if (!user && !permissions.canCreate) return;
    setSelectedUser(user);
    setShowUserModal(true);
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="recent-invoices">
      <div className="recent-invoices-header">
        <h1>Users <span className="text-muted">({pagination.total})</span></h1>
      </div>
      {error && <div className="recent-error">{error}</div>}
      <InlineNotice scope="users" />
      <div className="recent-invoices-list">
        <div className="recent-invoices-table-header">
          <div className="recent-invoices-actions" style={{ justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                onClick={handleReload}
                disabled={loading}
                className="recent-invoices-btn recent-invoices-btn-reload"
              >
                {loading ? (
                  <>
                    <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/><path d="M11 1a9 9 0 0 1 9 9"/></svg>
                    Loading...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>
                    Reload
                  </>
                )}
              </button>
              <button 
                onClick={handleViewUser}
                disabled={selectedRows.length !== 1}
                className="recent-invoices-btn recent-invoices-btn-view"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                View
              </button>
              {permissions.canEdit && (
                <button 
                  onClick={() => handleOpenUserModal(selectedRows.length === 1 ? users.find(u => u._id === selectedRows[0]) : null)}
                  disabled={selectedRows.length !== 1}
                  className="recent-invoices-btn recent-invoices-btn-copy"
                  title="Edit selected user"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                  Edit
                </button>
              )}
              {permissions.canDeactivate && (
                <button 
                  onClick={handleDeleteUser}
                  disabled={selectedRows.length !== 1}
                  className="recent-invoices-btn recent-invoices-btn-delete"
                  style={{ backgroundColor: '#ef4444', color: 'white' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                  Delete
                </button>
              )}
            </div>
            {permissions.canCreate && (
              <button
                onClick={() => handleOpenUserModal()}
                title="Add New User"
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add
              </button>
            )}
          </div>
        </div>
      <div className="table-responsive">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th style={{width: '3%'}}></th>
              <th style={{width: '5%'}}>#</th>
              <th onClick={() => handleSort('name')} style={{width: '25%', cursor: 'pointer'}}>Name {sortConfig.column === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th onClick={() => handleSort('email')} style={{width: '35%', cursor: 'pointer'}}>Email {sortConfig.column === 'email' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th onClick={() => handleSort('role')} style={{width: '15%', cursor: 'pointer'}}>Role {sortConfig.column === 'role' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th onClick={() => handleSort('createdAt')} style={{width: '20%', cursor: 'pointer'}}>Created Date {sortConfig.column === 'createdAt' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user, index) => (
              <tr 
                key={user._id}
                className={selectedRows.includes(user._id) ? 'selected' : ''}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(user._id)}
                    onChange={() => handleCheckboxChange(user._id)}
                  />
                </td>
                <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                <td style={{wordWrap: 'break-word', whiteSpace: 'normal'}}>{user.name}</td>
                <td style={{wordWrap: 'break-word', whiteSpace: 'normal', fontSize: '0.85rem'}}>{user.email || '-'}</td>
                <td style={{fontSize: '0.8rem'}}>{user.role || '-'}</td>
                <td style={{fontSize: '0.8rem'}}>{new Date(user.createdAt).toLocaleDateString('en-IN')}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {pagination.pages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First
              disabled={pagination.page === 1}
              onClick={() => handlePageChange(1)}
            />
            <Pagination.Prev
              disabled={pagination.page === 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            />
            
            {[...Array(pagination.pages)].map((_, index) => (
              <Pagination.Item
                key={index + 1}
                active={pagination.page === index + 1}
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </Pagination.Item>
            ))}
            
            <Pagination.Next
              disabled={pagination.page === pagination.pages}
              onClick={() => handlePageChange(pagination.page + 1)}
            />
            <Pagination.Last
              disabled={pagination.page === pagination.pages}
              onClick={() => handlePageChange(pagination.pages)}
            />
          </Pagination>
        </div>
      )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="Delete User"
        message={`Are you sure you want to delete "${userToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />

      {/* User Modal */}
      <UserModal
        show={showUserModal}
        onHide={() => {
          setShowUserModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onSave={handleSaveUser}
        title={selectedUser ? 'Edit User' : 'Add New User'}
      />
    </div>
  );
};

export default UserListPage;
